import { useCallback, useEffect, useRef, useState } from "react";
import agreementsService, { type Agreement } from "../services/agreementsService";

export interface UseUserAgreementsResult {
  agreements: Agreement[];
  isLoading: boolean;
  hasError: boolean;
  /** Re-runs the fetch for the current user. No-op while no user is selected. */
  retry: () => void;
}

const EMPTY_AGREEMENTS: Agreement[] = [];

/**
 * Loads a user's signed agreements through the admin agreements route.
 * Shared by the admin users sidebar and the client onboarding modal.
 */
export function useUserAgreements(userId: string | null): UseUserAgreementsResult {
  const [reloadToken, setReloadToken] = useState(0);
  const loadKey = userId === null ? null : `${userId}|${reloadToken}`;

  const [state, setState] = useState<{
    key: string | null;
    agreements: Agreement[];
    isLoading: boolean;
    hasError: boolean;
  }>({ key: null, agreements: EMPTY_AGREEMENTS, isLoading: false, hasError: false });

  // Monotonic token: this hook is keyed to a user-changeable selection (the
  // admin picks a row), so a slower earlier request must never write over a
  // newer one -- on the success path, the failure path, or the loading reset.
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestIdRef.current;

    if (userId === null || loadKey === null) {
      setState({
        key: null,
        agreements: EMPTY_AGREEMENTS,
        isLoading: false,
        hasError: false,
      });
      return;
    }

    setState({
      key: loadKey,
      agreements: EMPTY_AGREEMENTS,
      isLoading: true,
      hasError: false,
    });

    void agreementsService
      .getUserAgreements(userId)
      .then((loadedAgreements) => {
        if (requestId !== requestIdRef.current) return;
        setState({
          key: loadKey,
          agreements: loadedAgreements,
          isLoading: false,
          hasError: false,
        });
      })
      .catch((agreementsError: unknown) => {
        if (requestId !== requestIdRef.current) return;
        console.error("Failed to fetch agreements:", agreementsError);
        setState({
          key: loadKey,
          agreements: EMPTY_AGREEMENTS,
          isLoading: false,
          hasError: true,
        });
      });
  }, [loadKey, userId]);

  const retry = useCallback(() => {
    setReloadToken((currentReloadToken) => currentReloadToken + 1);
  }, []);

  // Until this render's key has been committed, report the pending state rather
  // than a previous selection's resolved agreements.
  if (state.key !== loadKey) {
    return {
      agreements: EMPTY_AGREEMENTS,
      isLoading: loadKey !== null,
      hasError: false,
      retry,
    };
  }

  return {
    agreements: state.agreements,
    isLoading: state.isLoading,
    hasError: state.hasError,
    retry,
  };
}
